import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes, { any } from 'prop-types';
import moment from 'moment-timezone';

import Img from '../../../components/Img';
import { Datepicker, Select } from '../../../components/Form';
import Popup from '../../../components/Popup';
import { drawScheduleTypeLabels } from '../../../utils/eventConstants';
import { dateFormat } from '../../../utils/courtSchedular';
import { apiDateFormat, getScheduledMatchesFromDayPlan, primaryViews } from '../../../utils/dayPlanner';
import {
    getDayPlannerSpecificData,
    toggleDayConfigSidePanel,
    toggleSummarySidePanel,
    updateSelectedScheduledVirtualDraw,
    copyDayToRealSchedule } from '../../../actions/day-planner-actions';

import styles from './dayPlanner.scss';

const DayPlannerHeader = (props) => {
    const {
        eventSchedule,
        onChangeDate,
    } = props;

    const dispatch = useDispatch();
    const eventScheduleTypeId = useSelector((state) => state.drawEdit?.event?.data?.scheduleTypeId);
    const dayPlanner = useSelector(({ dayPlanner }) => dayPlanner);
    const {
        dayConfig,
        viewState,
        eventPlan,
        dayPlan,
        enabledFacilitiesForDay,
        scheduledVirtualDraws,
        selectedScheduledVirtualDraw,
    } = dayPlanner;

    const { timeZone, eventStartUtc, eventEndUtc } = eventSchedule;

    const calendarSelectedDate = moment.tz(viewState.day, timeZone.value).startOf('day');
    const calendarStartDate = moment.utc(eventStartUtc).tz(timeZone.value).startOf('day');
    const calendarEndDate = moment.utc(eventEndUtc).tz(timeZone.value).startOf('day');

    const dayPlanHasScheduledMatches = getScheduledMatchesFromDayPlan(dayPlan).length > 0;

    const [showConfirmCopyDay, setShowConfirmCopyDay] = useState(false);

    // copy should set locked=true for the day, so,
    // the button should be disabled if either the day or if the whole plan are locked
    const dayPlanCanBeCopied = true;

    const scheduleTypeLabel = drawScheduleTypeLabels[eventScheduleTypeId].inline;

    const onFacilitySelected = (facilityId) => {
        dispatch(getDayPlannerSpecificData(eventPlan.tmsEventId, viewState.primaryView, viewState.day, facilityId));
    };

    const onVirtualDrawSelected = (virtualDrawId) => {
        dispatch(updateSelectedScheduledVirtualDraw(virtualDrawId));
    };

    const onDateChange = (date) => {
        onChangeDate(date.format(apiDateFormat));
    };

    const onGotoPreviousDayClick = () => {
        const previousDay = calendarSelectedDate.subtract(1, 'days');
        onDateChange(previousDay);
    };

    const onGotoNextDayClick = () => {
        const nextDay = calendarSelectedDate.add(1, 'days');
        onDateChange(nextDay);
    };

    const onClickSummary = () => {
        dispatch(toggleSummarySidePanel());
    };

    const onClickSettings = () => {
        dispatch(toggleDayConfigSidePanel());
    };

    const onClickCopyDay = () => {
        if (!eventPlan.locked && !dayConfig.locked) {
            setShowConfirmCopyDay(true);
        }
    };

    const onCloseConfirmCopyDay = () => {
        setShowConfirmCopyDay(false);
    };

    const onConfirmCopyDay = () => {
        setShowConfirmCopyDay(false);
        dispatch(copyDayToRealSchedule());
    };

    return (
        <>
            <div className={styles.dayPlannerGridHeader}>
                <Select
                    attributeToShow="name"
                    attributeToSave="id"
                    themeV3
                    emptyOption="All Draws"
                    options={scheduledVirtualDraws}
                    formGroupCssClass={styles.optionSelect}
                    controlled
                    value={selectedScheduledVirtualDraw}
                    onChange={onVirtualDrawSelected}
                    disabled={scheduledVirtualDraws.length <= 1}
                    data-testid="day-planner.header.draw-select"
                />
                <Select
                    attributeToShow="name"
                    attributeToSave="id"
                    themeV3
                    emptyOption="Select Facility"
                    options={enabledFacilitiesForDay}
                    formGroupCssClass={styles.optionSelect}
                    controlled
                    hiddenDefault={enabledFacilitiesForDay.length > 0}
                    value={viewState.facilityId}
                    onChange={onFacilitySelected}
                    disabled={enabledFacilitiesForDay.length <= 1}
                    data-testid="day-planner.header.facility-select"
                />
                <div className={styles.divider} />
                <button
                    className={`btn ${styles.dateSlider}`}
                    type="button"
                    onClick={onGotoPreviousDayClick}
                    disabled={calendarSelectedDate.isSame(calendarStartDate)}
                    data-testid="day-planner.header.prev-day-btn"
                >
                    <Img src="/img/v3/icons/icon-24px-arrow-left.svg" />
                </button>
                <Datepicker
                    className={`${styles.optionSelect} ${styles.dateSelect}`}
                    selected={calendarSelectedDate}
                    format={dateFormat}
                    minDate={calendarStartDate}
                    maxDate={calendarEndDate}
                    themeV3
                    onChange={onDateChange}
                    data-testid="day-planner.header.date-picker"
                />
                <button
                    className={`btn ${styles.dateSlider}`}
                    type="button"
                    onClick={onGotoNextDayClick}
                    disabled={calendarSelectedDate.isSame(calendarEndDate)}
                    data-testid="day-planner.header.next-day-btn"
                >
                    <Img src="/img/v3/icons/icon-24px-arrow-right.svg" />
                </button>
                <div className="flex-1" />
                {dayPlanHasScheduledMatches && (
                    <button
                        className={`btn btn-flex ${styles.settings} p0`}
                        type="button"
                        onClick={onClickSummary}
                        title={'Summary'}
                        data-testid="day-planner.header.summary-btn"
                    >
                        <Img className="mr4 mb2" src="/img/v3/icons/icon-18px-summary-teal.svg" />
                    </button>
                )}
                <button
                    className={`btn btn-flex ${styles.settings} p0`}
                    type="button"
                    onClick={onClickSettings}
                    title={'Set schedule'}
                    data-testid="day-planner.header.settings-btn"
                >
                    <Img className="mr4 mb2" src="/img/v3/icons/icon-18px-settings-teal.svg" />
                </button>
                {(dayPlanCanBeCopied && eventPlan && dayConfig && viewState.primaryView === primaryViews.DraftDraws) && (
                    <button
                        className={`btn btn-sm ${styles.copyButton}`}
                        type="button"
                        onClick={onClickCopyDay}
                        disabled={eventPlan.locked || dayConfig.locked || !dayConfig.scheduleReady || !dayPlan?.virtualDraws?.length}
                        data-testid="day-planner.header.copy-btn"
                    >
                        Copy
                    </button>
                )}
            </div>
            {showConfirmCopyDay && (
                <Popup
                    className="popup-sm"
                    popUpClassName={styles.confirmPopup}
                    onCloseClick={onCloseConfirmCopyDay}
                    closeOnClickOutside={true}
                    config={{
                        fullScreen: false,
                        noPadding: true,
                        scrollable: false,
                        showCloseIcon: false,
                    }}
                    data-testid="modal.day-planner.confirm-copy"
                >
                    <div className={styles.title} data-testid="modal.day-planner.confirm-copy.title">
                        Copy to {scheduleTypeLabel}?
                    </div>
                    <p className={styles.description} data-testid="modal.day-planner.confirm-copy.description">
                        This will copy updated draw start times to each draw,
                        allowing their use in {scheduleTypeLabel} scheduling.
                        This will lock the planner for the day.
                    </p>
                    <div className="d-flex mt16">
                        <button
                            className="btn btn-default flex-1 mr8"
                            type="button"
                            onClick={onCloseConfirmCopyDay}
                            data-testid="modal.day-planner.confirm-copy.cancel-btn"
                        >
                            No, cancel
                        </button>
                        <button
                            className="btn btn-primary flex-1"
                            type="button"
                            onClick={onConfirmCopyDay}
                            data-testid="modal.day-planner.confirm-copy.confirm-btn"
                        >
                            Yes, copy
                        </button>
                    </div>
                </Popup>
            )}
        </>
    );
};

DayPlannerHeader.propTypes = {
    eventSchedule: PropTypes.objectOf(any).isRequired,
    onChangeDate: PropTypes.func.isRequired,
};

DayPlannerHeader.defaultProps = {};

export default DayPlannerHeader;
